'use client'

import { useRouter, useSearchParams, usePathname } from 'next/navigation'

export type ModalType = 'source' | 'note' | 'insight'

// Map modal types to their corresponding URL parameter names
const MODAL_PARAM_MAP: Record<ModalType, string> = {
  source: 'source_id',
  note: 'note_id',
  insight: 'insight_id'
}

export function useModalManager() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  // Read current modal state from URL params
  const modalType = searchParams.get('modal') as ModalType | null

  // Get the modal ID based on the modal type
  const modalId = modalType ? searchParams.get(MODAL_PARAM_MAP[modalType]) : null

  /**
   * Open a modal by updating URL params without navigation
   * @param type - Type of modal to open (source, note, insight)
   * @param id - ID of the content to display
   */
  const openModal = (type: ModalType, id: string) => {
    const params = new URLSearchParams(searchParams.toString())
    params.set('modal', type)
    // Use the type-specific parameter name instead of 'id'
    params.set(MODAL_PARAM_MAP[type], id)
    // Use scroll: false to prevent page from scrolling when modal state changes
    router.push(`${pathname}?${params.toString()}`, { scroll: false })
  }

  /**
   * Close the currently open modal by removing modal params from URL
   */
  const closeModal = () => {
    const params = new URLSearchParams(searchParams.toString())

    // Remove the modal type parameter
    params.delete('modal')

    // Remove all possible modal ID parameters
    Object.values(MODAL_PARAM_MAP).forEach(param => {
      params.delete(param)
    })

    router.push(`${pathname}?${params.toString()}`, { scroll: false })
  }

  return {
    modalType,
    modalId,
    openModal,
    closeModal,
    isOpen: !!modalType && !!modalId
  }
}
